import { useCallback } from "react";

import {
  FloatingWindowInternal,
  useFloatingWindowPosition,
} from "src/base-components/FloatingWindow/FloatingWindow";
import {
  useFloatingWindowsActions,
  useIsFloatingWindowPinned,
} from "src/base-components/FloatingWindow/hooks";
import { FloatingWindowProps } from "src/base-components/FloatingWindow/store";
import { FileAttributes } from "src/clients/files";
import {
  FileDownloadButton,
  FileViewerContent,
  FileViewerTitle,
} from "src/files/FileViewer";

type FileViewerData = {
  file: FileAttributes | File;
};

export const FileViewerFloatingWindow: React.FC<FloatingWindowProps> = ({
  id,
  computePosition,
  data,
}) => {
  const { file } = data as FileViewerData;

  const { close, setPin } = useFloatingWindowsActions();
  const isPinned = useIsFloatingWindowPinned(id);
  const { position, windowRef } = useFloatingWindowPosition(computePosition);

  const closeWindow = useCallback(() => close(id), [close, id]);
  const togglePin = useCallback(
    () => setPin(id, !isPinned),
    [setPin, id, isPinned],
  );

  const contentType = file instanceof File ? file.type : file.content_type;
  const isFilePDF = contentType === "application/pdf";

  return (
    <FloatingWindowInternal
      ref={windowRef}
      height="h-112"
      id={id}
      isPinned={isPinned}
      minHeight="h-64"
      minWidth="w-96"
      style={{
        inset: "0px auto auto 0px",
        transform: position,
      }}
      title={<FileViewerTitle file={file} />}
      titleRight={isFilePDF ? null : <FileDownloadButton file={file} />}
      draggable
      maximizable
      resizeable
      onClose={closeWindow}
      onPin={togglePin}
    >
      <FileViewerContent file={file} />
    </FloatingWindowInternal>
  );
};
